/**
 * The inventory grid — everything owned, filterable by layer and season.
 *
 * A garment with no per-item photo renders as a colour swatch and is fully usable
 * everywhere: PRD-wardrobe.md section 4, stage 2. A missing image is never a blocked
 * feature, which is what let the wardrobe be catalogued before it was photographed.
 */
import { useMemo, useRef, useState, useEffect } from 'react';
import { api } from '../../lib/api';
import type { WardrobeCategory, WardrobeItem, WardrobeSummary } from '../../shared/types';
import ItemForm from './ItemForm';
import ItemTile from './ItemTile';
import {
  Button,
  Card,
  EmptyState,
  ErrorState,
  Loading,
  PanelHeading,
  Select,
  cx,
  useResource,
} from './ui';

interface Bundle {
  items: WardrobeItem[];
  categories: WardrobeCategory[];
  summary: WardrobeSummary;
}

type LayerFilter = 'all' | string;

export default function ItemsPanel() {
  const [editing, setEditing] = useState<WardrobeItem | null>(null);
  const [adding, setAdding] = useState(false);
  const [layer, setLayer] = useState<LayerFilter>('all');
  const [season, setSeason] = useState('all');

  const { data, loading, error, reload, setData } = useResource<Bundle>(async () => {
    const [items, categories, summary] = await Promise.all([
      api.get<WardrobeItem[]>('/wardrobe/items'),
      api.get<WardrobeCategory[]>('/wardrobe/categories'),
      api.get<WardrobeSummary>('/wardrobe/summary'),
    ]);
    return { items, categories, summary };
  }, []);

  const formRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (adding || editing) formRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [adding, editing]);

  const categoryLabel = useMemo(() => {
    const m = new Map((data?.categories ?? []).map((c) => [c.slug, c.label]));
    return (slug: string) => m.get(slug) ?? slug;
  }, [data]);

  const layerOf = useMemo(() => {
    const m = new Map((data?.categories ?? []).map((c) => [c.slug, c.layer]));
    return (slug: string) => m.get(slug) ?? 'accessory';
  }, [data]);

  const visible = useMemo(() => {
    let list = data?.items ?? [];
    if (layer !== 'all') list = list.filter((i) => layerOf(i.category) === layer);
    // No seasons recorded means "year round", so it must survive every season filter —
    // otherwise a filter silently hides the most versatile garments in the wardrobe.
    if (season !== 'all') {
      list = list.filter((i) => i.seasons.length === 0 || i.seasons.includes(season as never));
    }
    return list;
  }, [data, layer, season, layerOf]);

  const layers = useMemo(() => {
    const seen: string[] = [];
    for (const c of data?.categories ?? []) if (!seen.includes(c.layer)) seen.push(c.layer);
    return seen;
  }, [data]);

  function close() {
    setAdding(false);
    setEditing(null);
  }
  function afterSave() {
    close();
    reload();
  }

  /**
   * A photo upload has already committed and handed back the updated row, so this patches
   * what is on screen instead of refetching.
   *
   * Calling reload() here looked right and was wrong: it flips `loading` true, the render
   * below is gated on `data && !loading`, and the open form is inside that gate — so the
   * form unmounted and remounted on every upload, resetting the photo preview to the stale
   * row and silently discarding any half-typed edits in the other fields.
   *
   * `editing` is patched too. It is separate state holding the row the form was opened with,
   * and leaving it stale would resurrect the old photo the next time the subtree remounts.
   */
  function applyPhoto(updated: WardrobeItem) {
    setEditing((cur) => (cur && cur.id === updated.id ? updated : cur));
    setData((cur) => {
      if (!cur) return cur;
      const items = cur.items.map((i) => (i.id === updated.id ? updated : i));
      return {
        ...cur,
        items,
        // Recounted from the patched list rather than incremented — a replacement leaves the
        // count alone and a first photo raises it, without this needing to know which it was.
        summary: { ...cur.summary, with_photo: items.filter((i) => i.has_image).length },
      };
    });
  }

  return (
    <div>
      {loading && <Loading label="Loading wardrobe" />}
      {error && !loading && <ErrorState message={error} onRetry={reload} />}

      {data && !loading && !error && (
        <div className="space-y-5">
          <Counts summary={data.summary} />

          <PanelHeading
            title={`${visible.length} ${visible.length === 1 ? 'item' : 'items'}`}
            action={
              !adding && !editing && data.items.length > 0 ? (
                <Button variant="primary" onClick={() => setAdding(true)}>
                  Add an item
                </Button>
              ) : undefined
            }
          />

          {data.items.length > 0 && (
            <div className="flex flex-wrap items-center gap-3 text-sm text-ink-soft">
              <label className="flex items-center gap-2">
                <span>Layer</span>
                <Select value={layer} onChange={(e) => setLayer(e.target.value)} className="w-auto">
                  <option value="all">All</option>
                  {layers.map((l) => (
                    <option key={l} value={l}>
                      {l}
                    </option>
                  ))}
                </Select>
              </label>
              <label className="flex items-center gap-2">
                <span>Season</span>
                <Select value={season} onChange={(e) => setSeason(e.target.value)} className="w-auto">
                  <option value="all">All</option>
                  <option value="spring">Spring</option>
                  <option value="summer">Summer</option>
                  <option value="autumn">Autumn</option>
                  <option value="winter">Winter</option>
                </Select>
              </label>
            </div>
          )}

          {(adding || editing) && (
            <div ref={formRef}>
              <Card>
                <h2 className="mb-4 font-display text-lg font-semibold text-ink">
                  {editing ? `Edit — ${editing.name}` : 'New item'}
                </h2>
                <ItemForm
                  key={editing ? `edit-${editing.id}` : 'new'}
                  item={editing}
                  categories={data.categories}
                  onSaved={afterSave}
                  onCancel={close}
                  onPhotoChanged={applyPhoto}
                />
              </Card>
            </div>
          )}

          {data.items.length === 0 ? (
            <EmptyState
              title="Nothing catalogued yet"
              action={
                <Button variant="primary" onClick={() => setAdding(true)}>
                  Add the first item
                </Button>
              }
            >
              Add a few by hand to get going. Bulk intake from closet photos lands next, and
              will fill this in a single evening.
            </EmptyState>
          ) : visible.length === 0 ? (
            <EmptyState
              title="Nothing matches that filter"
              action={
                <Button
                  onClick={() => {
                    setLayer('all');
                    setSeason('all');
                  }}
                >
                  Clear filters
                </Button>
              }
            >
              You own {data.items.length} {data.items.length === 1 ? 'item' : 'items'}, none in
              this combination.
            </EmptyState>
          ) : (
            <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
              {visible.map((item) => (
                <ItemTile
                  key={item.id}
                  item={item}
                  categoryLabel={categoryLabel(item.category)}
                  onEdit={setEditing}
                />
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Counts stated plainly, photos included. Bulk intake produces garments with no per-item
 * image by design, so hiding that number would make an unfinished wardrobe look finished.
 */
function Counts({ summary }: { summary: WardrobeSummary }) {
  if (summary.total === 0) return null;
  const parts = [
    `${summary.total} ${summary.total === 1 ? 'item' : 'items'}`,
    `${summary.with_photo} with photos`,
  ];
  if (summary.flagged_retire > 0) parts.push(`${summary.flagged_retire} flagged to retire`);

  return (
    <Card tone="sunk" className="py-3">
      <p className="text-sm text-ink">{parts.join(' · ')}</p>
      {summary.by_layer.length > 0 && (
        <p className="mt-1 text-xs text-ink-soft">
          {summary.by_layer.map((l) => `${l.count} ${l.layer}`).join(' · ')}
        </p>
      )}
      {summary.with_photo < summary.total && (
        <p className={cx('mt-1 text-xs text-ink-soft')}>
          Items without a photo show as a colour swatch — they still work everywhere.
        </p>
      )}
    </Card>
  );
}
